import React from "react";
import { Container, Grid, Typography } from "@mui/material";
import { animateScroll as scroll } from "react-scroll";

import CoachData from "../../../../data/CoachData";
import ClientSummary from "../clients/ClientSummary";
import useEvents from "../../../../hooks/useEvents";

// Import CSS and new components
import "./CoachSummary.css";
import CoachHeader from "./components/CoachHeader";
import CoachConnectCodeCard from "./components/CoachConnectCodeCard";
import AddCoachDialog from "./AddCoachDialog";

const CoachSummary = React.memo((props) => {
  const [coach, setCoach] = React.useState(props.coach);
  const [mode, setMode] = React.useState("list");
  const [editCoachOpen, setEditCoachOpen] = React.useState(false);

  const selectedClient = React.useRef();
  const events = useEvents();

  React.useEffect(() => {
    setCoach(props.coach);
  }, [props.coach, props.update]); // eslint-disable-line

  React.useEffect(() => {
    const subscription = CoachData.getCoachSubscription(
      props.coach.uid,
      (snapshot) => {
        setCoach(snapshot);
      },
    );

    return () => {
      if (subscription) {
        subscription();
      }
    };
  }, [props.coach]);

  React.useEffect(() => {
    // Only run on client side
    if (typeof window !== "undefined") {
      scroll.scrollToTop({
        smooth: "linear",
        duration: 50,
      });
    }
  }, []);

  // Display loading state if coach data isn't available yet
  if (!coach && mode === "list") {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h6">Loading coach information...</Typography>
      </Container>
    );
  }

  if (mode === "list") {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <CoachHeader
          coach={coach}
          breadCrumb="Coaches"
          onBackClick={() => {
            if (props.onBackClick) {
              props.onBackClick();
            }
          }}
          onEditClick={() => {
            setEditCoachOpen(true);
            events.logButtonClick({
              buttonName: "Edit Coach",
              pageName: "Coaches",
              tabName: coach?.uid,
            });
          }}
          onAvatarUpdate={async () => {
            if (coach?.uid) {
              await CoachData.editCoach(coach.uid, {
                avatarChangedOn: new Date(),
              });
            }
          }}
        />

        <Grid container spacing={3} className="coach-info-grid">
          <Grid item xs={12} md={8} className="coach-connect-code-section">
            <CoachConnectCodeCard coach={coach} />
          </Grid>

          {/* We can add more cards here in the future, similar to ClientSummary */}
          <Grid item xs={12} md={4}>
            {/* Placeholder for future additional cards */}
          </Grid>
        </Grid>

        <AddCoachDialog
          mode="edit"
          coach={props.coach}
          userData={props.userData}
          open={editCoachOpen}
          onDialogClosed={(coachData) => {
            setEditCoachOpen(false);
            if (coachData) {
              CoachData.editCoach(props.coach.uid, coachData);
            }
          }}
        />
      </Container>
    );
  }

  if (mode === "details") {
    return (
      <ClientSummary
        breadCrumb={`${props.coach.firstName} ${props.coach.lastName}`}
        uid={selectedClient.current.uid}
        onBackClick={() => {
          selectedClient.current = null;
          setMode("list");
        }}
      />
    );
  }

  return null;
});

export default CoachSummary;
